/*
  Light or dark, chosen or inherited.

  CHEAP ONLY BECAUSE THE TOKENS CAME FIRST. `tokens.css` already carries a full dark block and a
  `prefers-color-scheme` fallback; all that was missing was somewhere to say "not that one". This is
  the whole of the switch.

  THREE STATES, NOT TWO. "System" is a real answer and the default: most people want the app to
  follow the machine, and a two-state toggle silently converts that into a preference the first time
  anybody clicks. `data-theme` on the root is what an explicit choice sets, and `tokens.css` scopes
  its media query `:not([data-theme='light'])` so the choice survives the OS flipping at sunset.

  `localStorage` IS THE RIGHT STORE HERE, and it is worth saying why in a product that deliberately
  keeps browser persistence to nothing. The rule this client follows is that PROJECT state lives on
  the server and per-user view state lives in `user_project_prefs` — both because they must survive a
  new machine and be shared or audited. A theme is neither: it belongs to this browser on this
  screen, it carries no engagement data, and putting it in `user_project_prefs` would mean a server
  round trip before the first paint and a dark-mode flash on every load.
*/
import { useEffect, useState } from 'react';

type Choice = 'system' | 'light' | 'dark';

const KEY = 'integrate:theme';

function read(): Choice {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw === 'light' || raw === 'dark' ? raw : 'system';
  } catch {
    // Private browsing, or a storage quota that is already full. Following the OS is a fine answer.
    return 'system';
  }
}

function apply(choice: Choice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

export function ThemeToggle(): JSX.Element {
  const [choice, setChoice] = useState<Choice>(read);

  useEffect(() => {
    apply(choice);
    try {
      if (choice === 'system') window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, choice);
    } catch {
      // The theme still applies for this session; only remembering it failed.
    }
  }, [choice]);

  /*
    Cycles rather than offering three buttons, because it is a preference somebody sets once and a
    three-way control in the header would cost more attention than it is worth. The label says which
    state it is IN, not what the click will do — a control that describes its own outcome reads as a
    command ("Dark") and leaves you unable to tell what is currently in force.
  */
  const next: Record<Choice, Choice> = { system: 'light', light: 'dark', dark: 'system' };
  const label: Record<Choice, string> = { system: 'Auto', light: 'Light', dark: 'Dark' };

  return (
    <button
      data-testid="theme-toggle"
      data-theme-choice={choice}
      title={`Appearance: ${label[choice]}. Click for ${label[next[choice]]}.`}
      aria-label={`Appearance: ${label[choice]}`}
      onClick={() => setChoice(next[choice])}
    >
      {label[choice]}
    </button>
  );
}
